import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css, styled } from 'styled-components';

import * as api from '../../../api';

import { ReactComponent as ArrowSvg } from '../../../assets/arrow.svg';
import { ReactComponent as MicSvg } from '../../../assets/mic.svg';
import soundgif from '../../../assets/soundwave.gif';
import { glow } from '../../../styles/animation';
import { ConfigurationContext } from '../../../context/configurationContext';
import { useIsInViewport } from '../../../hooks/useInViewport';

interface Props {
  value: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  autoFocus?: boolean;
  glowEnabled?: boolean;
  responsiveEnabled?: boolean;
  isConversationOpened?: boolean;
  onInputResize?: () => void;
}

const ChatInput: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  onKeyDown,
  onSubmit,
  autoFocus,
  onInputResize,
  glowEnabled = false,
  responsiveEnabled = true,
  isConversationOpened = false,
}) => {
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);
  const [transcripts, setTranscripts] = useState<string[]>([]);
  const [currentMessage, setCurrentMessage] = useState<
    { transcript: string; isFinal: boolean } | undefined
  >();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const configurationContext = useContext(ConfigurationContext);
  const isInViewport = useIsInViewport(wrapperRef);

  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return;
    }
    if (document.activeElement === inputRef.current) return;
    if (inputRef.current?.disabled) return;
    if (document.activeElement?.tagName === 'TEXTAREA') return;
    if (document.activeElement?.tagName === 'INPUT') return;
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;
    wrapperRef.current.dataset.replicatedValue = value;
  }, [value]);

  useEffect(() => {
    if (!isInViewport) return;
    if (!configurationContext?.onVisibleInViewport) return;
    configurationContext.onVisibleInViewport();
  }, [isInViewport, configurationContext]);

  const enableStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      startRecording(stream);
    } catch (err) {
      console.error(err);
    }
  };

  const onWebSocketMessage = (event: MessageEvent) => {
    const message: { transcript: string; isFinal: boolean } = JSON.parse(
      event.data,
    );
    setCurrentMessage(message);
  };

  const streamMicrophoneAudioToSocket = async (
    mediaStream: MediaStream,
    ws: WebSocket,
  ) => {
    const recorder = new MediaRecorder(mediaStream);
    ws.onmessage = onWebSocketMessage;
    setRecorder(recorder);
    recorder.start(500);
    recorder.addEventListener('dataavailable', ({ data }) => {
      if (ws.readyState !== ws.OPEN) return;
      ws.send(data);
    });

    if (recorder.state !== 'recording') {
      recorder.start();
    }
  };

  const startRecording = (mediaStream: MediaStream) => {
    const socket = api.client.Assistant.createSpeechToTextWebSocket();
    setIsRecording(true);
    setMediaStream(mediaStream);
    setSocket(socket);
    streamMicrophoneAudioToSocket(mediaStream, socket);
  };

  const stopRecording = useCallback(() => {
    if (!mediaStream) return;
    mediaStream.getTracks().forEach((track) => {
      track.stop();
    });
    const firstTrack = mediaStream.getAudioTracks()[0];
    firstTrack.stop();
    setMediaStream(null);
    setIsRecording(false);
    audioContext?.close();
    setAudioContext(null);
    recorder?.stop();
    setRecorder(null);
    socket?.close();
    setSocket(null);
    setTranscripts([]);
  }, [mediaStream, audioContext, recorder, socket]);

  const startRecordingAudio = () => {
    if (!mediaStream) {
      enableStream();
    } else {
      startRecording(mediaStream);
    }
  };

  const handleOnSubmit = () => {
    stopRecording();
    onSubmit();
  };

  useEffect(() => {
    if (!currentMessage) return;
    if (currentMessage.isFinal) {
      setTranscripts((prevState) => [...prevState, currentMessage.transcript]);
    }
    onChange([...transcripts, currentMessage.transcript].join(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMessage]);

  useEffect(() => {
    if (!autoFocus) return;
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [autoFocus, handleGlobalKeyDown]);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const getInputPlaceholder = () => {
    if (isRecording) {
      return 'Listening...';
    }
    if (glowEnabled && configurationContext?.inputTextHeroPlaceholder) {
      return configurationContext.inputTextHeroPlaceholder;
    }
    if (
      !glowEnabled &&
      !isConversationOpened &&
      configurationContext?.inputTextHeroPlaceholder
    ) {
      return configurationContext.inputTextHeroPlaceholder;
    }
    if (!glowEnabled && configurationContext?.inputTextMainPagePlaceholder) {
      return configurationContext.inputTextMainPagePlaceholder;
    }
    if (isConversationOpened) {
      return 'Add a response';
    }
    return 'Ask a question';
  };

  return (
    <Container>
      <TextAreaWrapper
        $glowEnabled={glowEnabled}
        $responsiveEnabled={responsiveEnabled}
        $isInputFocused={isInputFocused && !disabled}
        $isConversationOpened={isConversationOpened}
        ref={wrapperRef}
      >
        <TextAreaInput
          rows={1}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            onInputResize?.();
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          ref={inputRef}
          placeholder={getInputPlaceholder()}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
      </TextAreaWrapper>
      {!value.length && !isRecording && (
        <RecordButton
          type="button"
          disabled={disabled}
          onClick={() => !disabled && startRecordingAudio()}
          $isInputFocused={isInputFocused && !disabled}
        >
          <MicSvg />
        </RecordButton>
      )}
      {!value.length && isRecording && (
        <>
          {!value && (
            <>
              <SoundGifContainer>
                <SoundGif src={soundgif} />
              </SoundGifContainer>
            </>
          )}
          <RecordButton
            type="button"
            onClick={() => stopRecording()}
            $isInputFocused={false}
          >
            <MicSvg />
          </RecordButton>
        </>
      )}
      <SubmitButton
        type="button"
        $isVisible={!!value.length}
        onClick={handleOnSubmit}
        disabled={disabled}
        $isConversationOpened={isConversationOpened}
      >
        <ArrowRotate />
      </SubmitButton>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  min-height: var(--input-height);
`;

const SoundGifContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  top: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ArrowRotate = styled(ArrowSvg)`
  transform: rotate(45deg);
`;

const SoundGif = styled.img`
  width: 100%;
  height: 32px;
  margin-bottom: -5px;
  object-fit: contain;
`;

const RecordButton = styled.button.attrs<{ $isInputFocused: boolean }>(
  (props) => ({
    $isInputFocused: props.$isInputFocused ?? false,
  }),
)`
  position: absolute;
  right: 24px;
  bottom: 17px;
  background-color: transparent;
  border: none;
  height: 30px;
  width: 30px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    overflow: visible;
    path {
      transition: fill 0.4s ease;
      fill: ${(props) =>
        props.$isInputFocused ? 'var(--action-color)' : '#BFBFBF'};
    }
  }

  &:hover {
    opacity: 0.8;
  }
`;

const SubmitButton = styled.button.attrs<{
  $isVisible: boolean;
  $isConversationOpened: boolean;
}>((props) => ({
  $isVisible: props.$isVisible ?? false,
  $isConversationOpened: props.$isConversationOpened ?? false,
}))`
  -webkit-appearance: none;
  appearance: none;
  position: absolute;
  right: 24px;
  bottom: 17px;
  background-color: var(--action-color);
  border: none;
  height: 30px;
  width: 30px;
  border-radius: 5px;
  transition: ${(props) =>
    props.$isVisible ? 'opacity 0.4s ease, transform 0.4s ease' : ''};
  opacity: ${(props) => (props.$isVisible ? '1' : '0')};
  pointer-events: ${(props) => (props.$isVisible ? 'auto' : 'none')};
  transform: ${(props) => (props.$isVisible ? 'scale(1)' : 'scale(0.8)')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    overflow: visible;
    transform: ${(props) =>
      props.$isConversationOpened ? 'rotate(0deg)' : 'none'};
  }
`;

const TextAreaWrapper = styled.div.attrs<{
  $glowEnabled: boolean;
  $responsiveEnabled: boolean;
  $isInputFocused: boolean;
  $isConversationOpened: boolean;
}>((props) => ({
  $glowEnabled: props.$glowEnabled ?? false,
  $responsiveEnabled: props.$responsiveEnabled ?? true,
  $isInputFocused: props.$isInputFocused ?? false,
  $isConversationOpened: props.$isConversationOpened ?? false,
}))`
  display: grid;
  background-color: ${(props) =>
    props.$glowEnabled
      ? 'linear-gradient(45deg, var(--gradient-one-color), var(--gradient-two-color), var(--gradient-one-color), var(--gradient-two-color), var(--gradient-one-color), var(--gradient-two-color))'
      : props.$isInputFocused
        ? 'var(--action-color)'
        : 'var(--border-color)'};
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: 300% 300%;
  animation: ${(props) =>
    props.$glowEnabled
      ? css`
          ${glow} 5s linear infinite
        `
      : 'none'};
  transition: background-color 0.4s ease;
  border-radius: var(--input-border-radius);
  padding: var(--input-padding);

  @media (max-width: 680px) {
    ${(props) =>
      props.$responsiveEnabled &&
      css`
        background: transparent !important;
        padding: 0;
      `}
  }

  &::after {
    content: attr(data-replicated-value) ' ';
    white-space: pre-wrap;
    visibility: hidden;
  }

  &::after,
  > textarea {
    width: 100%;
    font: inherit;
    outline: none;
    border: none;
    padding-right: 60px;
    padding-left: 24px;
    padding-top: calc(
      calc(var(--input-height) / 2) - calc(
          (var(--conversation-font-size-desktop) + 6px) / 2
        )
    );
    padding-bottom: calc(
      calc(var(--input-height) / 2) - calc(
          (var(--conversation-font-size-desktop) + 6px) / 2
        )
    );
    min-height: var(--input-height);
    max-height: 120px;
    max-width: 100vw;
    grid-area: 1 / 1 / 2 / 2;
    border-radius: calc(var(--input-border-radius) - var(--input-padding));
    overflow-y: visible;
    font-family: ${(props) =>
      props.$isConversationOpened
        ? 'var(--conversation-font-family)'
        : 'var(--hero-input-font-family)'};
    font-size: ${(props) =>
      props.$isConversationOpened
        ? 'var(--conversation-font-size-desktop)'
        : 'var(--hero-input-font-size-desktop)'};
    line-height: ${(props) =>
      props.$isConversationOpened
        ? 'calc(var(--conversation-font-size-desktop) + 3px)'
        : 'calc(var(--hero-input-font-size-desktop) + 3px)'};

    @media (max-width: 680px) {
      ${(props) =>
        props.$responsiveEnabled &&
        css`
          font-size: ${props.$isConversationOpened
            ? 'var(--conversation-font-size-mobile)'
            : 'var(--hero-input-font-size-mobile)'};
          border-top: 1px solid var(--border-color);
          border-radius: ${props.$isConversationOpened ? '0px' : 'inherit'};
        `}
    }
  }

  > textarea {
    &:disabled {
      background-color: var(--background-color);
    }

    @media (max-width: 680px) {
      ${(props) =>
        props.$responsiveEnabled &&
        css`
          &:focus {
            border-top: ${props.$isConversationOpened
              ? '1px solid var(--action-color)'
              : 'inherit'};
          }
        `}
    }
  }
`;

const TextAreaInput = styled.textarea`
  resize: none;
`;

export default ChatInput;
